import React, { useEffect, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faSearch, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { HR_NAV } from './nav';

const Directory: React.FC = () => {
    const { api } = useInternalApi();
    const [staff, setStaff] = useState<any[]>([]);
    const [byDept, setByDept] = useState<any[]>([]);
    const [external, setExternal] = useState<any>({});
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    useEffect(() => { document.title = 'People Directory - Uzima Internal'; }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            const r = await api.get(`hr-api/directory/?${params}`);
            if (r.data?.status === 'success') {
                setStaff(r.data.data.internal_staff || []);
                setByDept(r.data.data.by_department || []);
                setExternal(r.data.data.external_workforce || {});
                setNote(r.data.data.note || '');
            }
        } finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <InternalShell activeDashboard="human_resources" subNav={HR_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />People Directory</h2>
            <p className="text-muted mb-4">Internal platform staff, plus the wider workforce on the platform</p>

            <div className="alert alert-secondary small"><FontAwesomeIcon icon={faInfoCircle} className="me-2" />{note}</div>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Internal staff</div><h4>{staff.length}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Clinic staff (external)</div><h4 className="text-muted">{external.clinic_staff ?? 0}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Practitioners (external)</div><h4 className="text-muted">{external.individual_practitioners ?? 0}</h4>
                        </div></div></div>
                        <div className="col-md-3 mb-3"><div className="card border-0 shadow-sm"><div className="card-body">
                            <div className="text-muted small mb-1">Clinics</div><h4 className="text-muted">{external.clinics ?? 0}</h4>
                        </div></div></div>
                    </div>

                    <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
                        <div className="input-group" style={{ maxWidth: 340 }}>
                            <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
                            <input className="form-control" placeholder="Search internal staff…" value={search}
                                onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
                            <button className="btn btn-outline-primary" onClick={load}>Search</button>
                        </div>
                        {byDept.map(d => (
                            <span key={d.department} className="badge bg-light text-dark border p-2">{d.department}: {d.count}</span>
                        ))}
                    </div>

                    <div className="card border-0 shadow-sm">
                        <div className="card-header bg-white"><strong>Internal staff</strong></div>
                        <div className="card-body p-0">
                            {staff.length === 0 ? (
                                <div className="text-center py-5 text-muted">No internal staff match.</div>
                            ) : (
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light"><tr><th>Name</th><th>Department</th><th>Role</th><th>Last login</th><th>Open training</th><th>Status</th></tr></thead>
                                    <tbody>
                                        {staff.map(p => (
                                            <tr key={p.id}>
                                                <td><strong>{p.full_name}</strong><div className="small text-muted">{p.email}</div></td>
                                                <td>{p.department_label}</td>
                                                <td>{p.role_label}</td>
                                                <td className="small">{p.last_login_at ? new Date(p.last_login_at).toLocaleDateString() : 'Never'}</td>
                                                <td>{p.open_training > 0 ? <span className="badge bg-warning">{p.open_training}</span> : '—'}</td>
                                                <td>
                                                    {p.on_leave_today && <span className="badge bg-info me-1">On leave</span>}
                                                    <span className={`badge ${p.active ? 'bg-success' : 'bg-secondary'}`}>{p.active ? 'Active' : 'Inactive'}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default Directory;
